import React, { useState, useMemo, useCallback } from 'react';
import {
  StockTransaction,
  MaterialCatalog,
  MaterialCategory,
  TransactionType,
  TransactionStatus,
  AdjustmentReason,
} from '@models/warehouse.model';
import { TransactionTypeBadge } from '@components/atoms/TransactionTypeBadge';
import { Badge } from '@components/atoms/Badge';
import { Loading } from '@components/atoms/Loading';
import { Tab } from '@components/atoms/Tab';
import { EmptyData } from '@components/molecules/EmptyData';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { SingleValue, MultiValue } from 'react-select';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDateOnly } from '@utils/formatDate';

interface TransactionHistoryTableProps {
  transactions: StockTransaction[];
  materials: MaterialCatalog[];
  categories?: MaterialCategory[];
  isLoading?: boolean;
}

type FilterTab = 'all' | 'receive' | 'consume' | 'adjust' | 'other';

const ITEMS_PER_PAGE = 10;

const REASON_LABELS: Record<AdjustmentReason, string> = {
  [AdjustmentReason.COUNT_CORRECTION]: 'Count Correction',
  [AdjustmentReason.DAMAGED]: 'Damaged',
  [AdjustmentReason.LOST]: 'Lost',
  [AdjustmentReason.STOLEN]: 'Stolen',
  [AdjustmentReason.BROKEN]: 'Broken',
  [AdjustmentReason.EXPIRED]: 'Expired',
  [AdjustmentReason.FOUND]: 'Found',
  [AdjustmentReason.TRANSFER]: 'Transfer',
  [AdjustmentReason.OTHER]: 'Other',
};

const transactionTypes: TransactionType[] = [
  TransactionType.RECEIVE,
  TransactionType.CONSUME,
  TransactionType.ADJUST,
  TransactionType.RESERVE,
  TransactionType.RELEASE_RESERVE,
  TransactionType.RETURN,
  TransactionType.RECOVERY,
  TransactionType.TRANSFER,
  TransactionType.USE_AS_EQUIPMENT,
];

export const TransactionHistoryTable: React.FC<TransactionHistoryTableProps> = ({
  transactions,
  materials,
  categories = [],
  isLoading = false,
}) => {
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<TransactionType | ''>('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const getMaterialName = useCallback(
    (catalogId?: string) => {
      if (!catalogId) return null;
      return materials.find((m) => m.catalogId === catalogId);
    },
    [materials],
  );

  // Extract unique users, projects, tasks from data
  const userOptions = useMemo(() => {
    const map = new Map<string, string>();
    transactions.forEach((t) => {
      if (t.user?.userId) {
        map.set(t.user.userId, `${t.user.firstName} ${t.user.lastName}`);
      }
    });
    return Array.from(map, ([value, label]) => ({ value, label }));
  }, [transactions]);

  const projectOptions = useMemo(() => {
    const map = new Map<string, string>();
    transactions.forEach((t) => {
      if (t.project?.projectId) {
        map.set(t.project.projectId, t.project.name);
      }
    });
    return Array.from(map, ([value, label]) => ({ value, label }));
  }, [transactions]);

  const taskOptions = useMemo(() => {
    const map = new Map<string, string>();
    transactions.forEach((t) => {
      if (t.task?.taskId) {
        if (selectedProjectId && t.project?.projectId !== selectedProjectId) return;
        map.set(t.task.taskId, t.task.name);
      }
    });
    return Array.from(map, ([value, label]) => ({ value, label }));
  }, [transactions, selectedProjectId]);

  // Apply filters
  const filteredTransactions = useMemo(() => {
    return transactions.filter((transaction) => {
      if (activeTab === 'receive' && transaction.type !== TransactionType.RECEIVE) return false;
      if (activeTab === 'consume' && transaction.type !== TransactionType.CONSUME) return false;
      if (activeTab === 'adjust' && transaction.type !== TransactionType.ADJUST) return false;
      if (activeTab === 'other') {
        const otherTypes = [
          TransactionType.RESERVE, TransactionType.RELEASE_RESERVE,
          TransactionType.RETURN, TransactionType.RECOVERY,
          TransactionType.TRANSFER, TransactionType.USE_AS_EQUIPMENT,
        ];
        if (!otherTypes.includes(transaction.type)) return false;
      }

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const material = getMaterialName(transaction.catalogId);
        const matchesMaterial = material?.name.toLowerCase().includes(query);
        const matchesRef = transaction.referenceNumber?.toLowerCase().includes(query);
        const matchesNotes = transaction.notes?.toLowerCase().includes(query);
        const matchesUser = transaction.user
          ? `${transaction.user.firstName} ${transaction.user.lastName}`.toLowerCase().includes(query)
          : false;
        if (!matchesMaterial && !matchesRef && !matchesNotes && !matchesUser) return false;
      }

      if (selectedCategoryId) {
        const material = getMaterialName(transaction.catalogId);
        if (!material || material.categoryId !== selectedCategoryId) return false;
      }

      if (selectedType && transaction.type !== selectedType) return false;
      if (selectedUserId && transaction.user?.userId !== selectedUserId) return false;
      if (selectedProjectId && transaction.project?.projectId !== selectedProjectId) return false;
      if (selectedTaskId && transaction.task?.taskId !== selectedTaskId) return false;

      if (startDate && transaction.createdAt) {
        if (new Date(transaction.createdAt) < new Date(startDate)) return false;
      }
      if (endDate && transaction.createdAt) {
        const filterEnd = new Date(endDate);
        filterEnd.setHours(23, 59, 59, 999);
        if (new Date(transaction.createdAt) > filterEnd) return false;
      }

      return true;
    });
  }, [transactions, activeTab, searchQuery, selectedType, selectedCategoryId, selectedUserId, selectedProjectId, selectedTaskId, startDate, endDate, getMaterialName]);

  const sortedTransactions = useMemo(() => {
    return [...filteredTransactions].sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    });
  }, [filteredTransactions]);

  const totalPages = Math.ceil(sortedTransactions.length / ITEMS_PER_PAGE);
  const paginatedTransactions = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedTransactions.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedTransactions, currentPage]);

  const stats = useMemo(() => ({
    total: transactions.length,
    receive: transactions.filter((t) => t.type === TransactionType.RECEIVE).length,
    consume: transactions.filter((t) => t.type === TransactionType.CONSUME).length,
    adjust: transactions.filter((t) => t.type === TransactionType.ADJUST).length,
  }), [transactions]);

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const getStatusVariant = (status: TransactionStatus): 'success' | 'warning' | 'secondary' => {
    switch (status) {
      case TransactionStatus.COMPLETED: return 'success';
      case TransactionStatus.PENDING: return 'warning';
      default: return 'secondary';
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleTabChange = (tab: FilterTab) => { setActiveTab(tab); setCurrentPage(1); };
  const handleSearchChange = (value: string) => { setSearchQuery(value); setCurrentPage(1); };

  const hasActiveFilters = searchQuery || selectedType || selectedCategoryId || selectedUserId || selectedProjectId || selectedTaskId || startDate || endDate;

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedType('');
    setSelectedCategoryId('');
    setSelectedUserId('');
    setSelectedProjectId('');
    setSelectedTaskId('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const handleSelectChange = (setter: (val: string) => void) =>
    (option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
      if (option && !Array.isArray(option) && 'value' in option) {
        setter(String(option.value));
      } else {
        setter('');
      }
      setCurrentPage(1);
    };

  if (isLoading) return <Loading size="xl" center />;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
            </div>
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <SwapHorizIcon className="text-indigo-600" sx={{ fontSize: 20 }} />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Received</p>
              <p className="text-2xl font-semibold text-green-600">{stats.receive}</p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <span className="text-green-600 font-bold">↓</span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Consumed</p>
              <p className="text-2xl font-semibold text-blue-600">{stats.consume}</p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <span className="text-blue-600 font-bold">→</span>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Adjusted</p>
              <p className="text-2xl font-semibold text-purple-600">{stats.adjust}</p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <span className="text-purple-600 font-bold">±</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4 space-y-4">
        <Tab
          tabs={[
            { id: 'all', name: 'All' },
            { id: 'receive', name: 'Received', count: stats.receive > 0 ? stats.receive : undefined },
            { id: 'consume', name: 'Consumed', count: stats.consume > 0 ? stats.consume : undefined },
            { id: 'adjust', name: 'Adjusted', count: stats.adjust > 0 ? stats.adjust : undefined },
            { id: 'other', name: 'Other' },
          ]}
          activeTab={activeTab}
          onTabChange={(tabId) => handleTabChange(tabId as FilterTab)}
          variant="underline"
        />

        {/* Filters Row 1 */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:w-auto sm:min-w-[200px] sm:max-w-[260px]">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" sx={{ fontSize: 18 }} />
            <input
              type="text"
              placeholder="Search materials, references..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full border border-gray-300 rounded-md py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className="w-full sm:w-[170px]">
            <ReactSelect
              options={transactionTypes.map((type) => ({ value: type, label: type.replace(/_/g, ' ') }))}
              value={selectedType ? { value: selectedType, label: selectedType.replace(/_/g, ' ') } : null}
              onChange={handleSelectChange((v) => setSelectedType(v as TransactionType | ''))}
              placeholder="All Types"
              isClearable
            />
          </div>

          {categories.length > 0 && (
            <div className="w-full sm:w-[170px]">
              <ReactSelect
                options={categories.map((cat) => ({ value: cat.categoryId, label: cat.name }))}
                value={selectedCategoryId ? { value: selectedCategoryId, label: categories.find((c) => c.categoryId === selectedCategoryId)?.name || '' } : null}
                onChange={handleSelectChange(setSelectedCategoryId)}
                placeholder="All Categories"
                isClearable
              />
            </div>
          )}

          <div className="flex items-center gap-2">
            <div className="relative">
              <CalendarTodayIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" sx={{ fontSize: 14 }} />
              <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
                className="w-[140px] border border-gray-300 rounded-md py-2 pl-9 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <span className="text-sm text-gray-400">to</span>
            <div className="relative">
              <CalendarTodayIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" sx={{ fontSize: 14 }} />
              <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
                className="w-[140px] border border-gray-300 rounded-md py-2 pl-9 pr-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
          </div>
        </div>

        {/* Filters Row 2 */}
        <div className="flex flex-wrap items-center gap-3">
          {userOptions.length > 0 && (
            <div className="w-full sm:w-[200px]">
              <ReactSelect
                options={userOptions}
                value={selectedUserId ? { value: selectedUserId, label: userOptions.find((u) => u.value === selectedUserId)?.label || '' } : null}
                onChange={handleSelectChange(setSelectedUserId)}
                placeholder="All Users"
                isClearable
              />
            </div>
          )}

          {projectOptions.length > 0 && (
            <div className="w-full sm:w-[200px]">
              <ReactSelect
                options={projectOptions}
                value={selectedProjectId ? { value: selectedProjectId, label: projectOptions.find((p) => p.value === selectedProjectId)?.label || '' } : null}
                onChange={(option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
                  if (option && !Array.isArray(option) && 'value' in option) {
                    setSelectedProjectId(String(option.value));
                  } else {
                    setSelectedProjectId('');
                  }
                  setSelectedTaskId('');
                  setCurrentPage(1);
                }}
                placeholder="All Projects"
                isClearable
              />
            </div>
          )}

          {taskOptions.length > 0 && (
            <div className="w-full sm:w-[200px]">
              <ReactSelect
                options={taskOptions}
                value={selectedTaskId ? { value: selectedTaskId, label: taskOptions.find((t) => t.value === selectedTaskId)?.label || '' } : null}
                onChange={handleSelectChange(setSelectedTaskId)}
                placeholder="All Tasks"
                isClearable
              />
            </div>
          )}

          {hasActiveFilters && (
            <button type="button" onClick={clearFilters} className="text-sm text-indigo-600 hover:text-indigo-700 font-medium whitespace-nowrap">
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Cards List */}
      {sortedTransactions.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 overflow-hidden">
          <EmptyData
            description={
              hasActiveFilters || activeTab !== 'all'
                ? 'No transactions found. Try adjusting your filters.'
                : 'No transactions yet. Transactions will appear here when stock movements occur.'
            }
          />
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {paginatedTransactions.map((transaction) => {
              const material = getMaterialName(transaction.catalogId);
              const isCancelled = transaction.status === TransactionStatus.CANCELLED;
              const isPositive = [TransactionType.RECEIVE, TransactionType.RETURN, TransactionType.RECOVERY, TransactionType.RELEASE_RESERVE].includes(transaction.type);
              const sign = isPositive ? '+' : '-';
              const qtyColor = isPositive ? 'text-green-600' : 'text-red-600';

              return (
                <div
                  key={transaction.transactionId}
                  className={`bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4 ${isCancelled ? 'opacity-60' : ''}`}
                >
                  {/* Row 1: Type badge + date + status */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <TransactionTypeBadge type={transaction.type} size="sm" />
                      <span className="text-sm text-gray-500">
                        {transaction.createdAt ? formatDateOnly(transaction.createdAt) : '-'}
                        {transaction.createdAt && (
                          <span className="ml-1 text-xs">
                            {new Date(transaction.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                          </span>
                        )}
                      </span>
                    </div>
                    <Badge size="sm" variant={getStatusVariant(transaction.status)}>
                      {transaction.status}
                    </Badge>
                  </div>

                  {/* Row 2: Material info + quantity */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-semibold ${isCancelled ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                        {material?.name || 'Unknown Material'}
                      </p>
                      {material?.sku && <p className="text-xs text-gray-400">{material.sku}</p>}
                    </div>
                    <div className="text-right ml-4">
                      <p className={`text-sm font-semibold ${isCancelled ? 'line-through text-gray-400' : qtyColor}`}>
                        {sign}{Math.abs(transaction.quantity)} {material?.unit || ''}
                      </p>
                      {transaction.totalCost != null && transaction.totalCost !== 0 && (
                        <p className={`text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                          {isPositive ? '+' : '-'}{formatCurrency(Math.abs(transaction.totalCost))}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Row 3: Details chips */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                    {transaction.user && (
                      <span>
                        <span className="text-gray-400">By:</span>{' '}
                        <span className="text-gray-700">{transaction.user.firstName} {transaction.user.lastName}</span>
                      </span>
                    )}
                    {transaction.project && (
                      <span>
                        <span className="text-gray-400">Project:</span>{' '}
                        <span className="text-gray-700">{transaction.project.name}</span>
                      </span>
                    )}
                    {transaction.task && (
                      <span>
                        <span className="text-gray-400">Task:</span>{' '}
                        <span className="text-gray-700">{transaction.task.name}</span>
                      </span>
                    )}
                    {transaction.reason && (
                      <span>
                        <span className="text-gray-400">Reason:</span>{' '}
                        <span className="text-gray-700">{REASON_LABELS[transaction.reason as AdjustmentReason] || transaction.reason}</span>
                      </span>
                    )}
                    {transaction.supplier && (
                      <span>
                        <span className="text-gray-400">Supplier:</span>{' '}
                        <span className="text-gray-700">{transaction.supplier}</span>
                      </span>
                    )}
                    {transaction.referenceNumber && (
                      <span>
                        <span className="text-gray-400">Ref:</span>{' '}
                        <span className="text-gray-700">{transaction.referenceNumber}</span>
                      </span>
                    )}
                    {transaction.unitCost != null && (
                      <span>
                        <span className="text-gray-400">Unit Cost:</span>{' '}
                        <span className="text-gray-700">{formatCurrency(transaction.unitCost)}</span>
                      </span>
                    )}
                  </div>

                  {/* Notes */}
                  {transaction.notes && (
                    <p className="text-xs text-gray-400 mt-2 italic truncate">
                      {transaction.notes}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 px-4 py-4 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing <span className="font-medium text-gray-900">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> to{' '}
                <span className="font-medium text-gray-900">{Math.min(currentPage * ITEMS_PER_PAGE, sortedTransactions.length)}</span> of{' '}
                <span className="font-medium text-gray-900">{sortedTransactions.length}</span> transactions
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1">
                  <ChevronLeft className="h-4 w-4" /> Previous
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map((pageNum) => (
                  <button type="button" key={pageNum} onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium ${currentPage === pageNum ? 'bg-indigo-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                    {pageNum}
                  </button>
                ))}
                {totalPages > 5 && (
                  <>
                    <span className="text-gray-500">...</span>
                    <button type="button" onClick={() => handlePageChange(totalPages)}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium ${currentPage === totalPages ? 'bg-indigo-600 text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                      {totalPages}
                    </button>
                  </>
                )}
                <button type="button" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1">
                  Next <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TransactionHistoryTable;
